import type { ECCOClubBenefitModel } from "@lib/contentful/mappers/parts/eccoClubBenefit/types";
import { SimpleGrid } from "@ecco/ui/simple-grid";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { Text } from "@ecco/ui/text";
import { NextImage } from "@ecco/ui/next-image";
import { getBenefitIcon } from "@components/auth/sign-up/components/benefits-list/helpers/getBenefitIcon";
import type { FC, ReactNode } from "react";

const BENEFIT_ICON_SIZE = 38;

interface BenefitsGridProps {
    title?: string;
    description?: string;
    benefits: ECCOClubBenefitModel[];
    links?: ReactNode;
}

export const BenefitsGrid: FC<BenefitsGridProps> = ({ title, description, benefits, links }) => {
    return (
        <Flex flexDirection="column" bg="white" w="full" h="100%">
            <Flex flexDirection="column" flex={1} p={["3", "4", "6"]} justifyContent="flex-end">
                {title && (
                    <Text fontWeight="bold" fontSize={["mobileH1", "desktopH2"]} mb={["3", "4"]}>
                        {title}
                    </Text>
                )}
                {description && (
                    <Text
                        fontSize={["mobileBodyB1", "desktopH3"]}
                        fontWeight="bold"
                        mb={["4", "6"]}
                    >
                        {description}
                    </Text>
                )}
                <SimpleGrid columns={2} spacingX="16px" spacingY={["4", "8"]}>
                    {benefits.map((benefit) => {
                        const iconSrc = getBenefitIcon(benefit.iconType);

                        return (
                            <Flex key={benefit.id} gap="16px" alignItems="center">
                                <Box
                                    flexShrink={0}
                                    w={`${BENEFIT_ICON_SIZE}px`}
                                    h={`${BENEFIT_ICON_SIZE}px`}
                                >
                                    <NextImage
                                        src={iconSrc}
                                        alt=""
                                        width={BENEFIT_ICON_SIZE}
                                        height={BENEFIT_ICON_SIZE}
                                        unoptimized
                                    />
                                </Box>
                                <Text
                                    fontWeight="bold"
                                    fontSize={["mobileBodyB2", "desktopBodyTextNormal"]}
                                >
                                    {benefit.description}
                                </Text>
                            </Flex>
                        );
                    })}
                </SimpleGrid>
                {links && <Box pt={["4", "6"]}>{links}</Box>}
            </Flex>
        </Flex>
    );
};
